import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  togglePinnedFiltersState,
  selectCreditCheckPinnedFilters,
} from '@store';
import { useHasFilters } from 'hooks/common';
import {
  CREDIT_CHECK_TABLE_STAGES,
  CREDIT_CHECK_STAGE_ENUMS,
} from 'components/creditCheckTable/constants';

const parseQueryValue = (value) => {
  if (!value) return [];
  if (typeof value === 'string') {
    return value.split(',').filter(Boolean);
  }
  return Array.isArray(value) ? value : [];
};

const originOptions = [
  { text: 'HQ', value: 'hq' },
  { text: 'FORM', value: 'form' },
  { text: 'Contact form', value: 'contact-form' },
  { text: 'instaride:form', value: 'instaride:form' },
  { text: 'instaride', value: 'instaride' },
  { text: 'instatrade:form', value: 'instatrade:form' },
];

const contractTypeOptions = [
  { text: 'Open', value: 'open' },
  { text: 'Fixed', value: 'fixed' },
];

export const useCreditCheckFilterConfig = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectCreditCheckPinnedFilters);

  const stages = useMemo(
    () =>
      Object.keys(CREDIT_CHECK_STAGE_ENUMS).map((key) => ({
        value: CREDIT_CHECK_STAGE_ENUMS[key],
        text: CREDIT_CHECK_TABLE_STAGES[key].label,
      })),
    []
  );

  const filterCategories = useMemo(
    () => [
      {
        id: 'origin',
        label: 'Origin',
        icon: RadioButtonUncheckedIcon,
        options: originOptions,
        multiSelect: true,
      },
      {
        id: 'stage',
        label: 'Stage',
        icon: RadioButtonUncheckedIcon,
        options: stages,
        multiSelect: true,
      },
      {
        id: 'contractType',
        label: 'Contract Type',
        icon: RadioButtonUncheckedIcon,
        options: contractTypeOptions,
        multiSelect: true,
      },
      {
        id: 'date',
        label: 'Created At',
        icon: RadioButtonUncheckedIcon,
        type: 'dateTimeRange',
        useISOFormat: true,
      },
    ],
    [stages]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'creditCheck', pinnedFilter }));
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
  });

  const currentFilters = useMemo(
    () => ({
      origin: parseQueryValue(router.query.origin),
      stage: parseQueryValue(router.query.stage),
      contractType: parseQueryValue(router.query.contractType),
      date: {
        dateFrom: router.query.dateFrom || null,
        dateTo: router.query.dateTo || null,
      },
    }),
    [router.query]
  );

  return {
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
  };
};
